import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import {
  Search as SearchIcon,
  TwoWheeler as RiderIcon,
  DragIndicator as DragIcon,
  Route as RouteIcon
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import {
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useApp } from '../../context/AppContext';
import { appConfig } from '../../config/app';

// Sortable table row component
const SortableTableRow = ({ orderId, index, orderDetail, moveDelta }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: orderId });

  const moved = moveDelta !== 0 && moveDelta !== undefined;
  const movedUp = moved && moveDelta < 0;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const getTimeSlotDisplay = (order) => {
    if (!order) return 'Loading...';
    return order.timeSlotDetails.startTime + ' - ' + order.timeSlotDetails.endTime;
  };

  const getDeliveryAddress = (order) => {
    if (!order) return 'Loading...';
    
    if (order.deliveryAddress) {
      const parts = [
        order.deliveryAddress.houseDetails,
        order.deliveryAddress.streedDetails,
        order.deliveryAddress.landmark,
      ].filter(Boolean);
      
      return parts.length > 0 ? parts.join(', ') : 'No address';
    }
    
    if (order.delivery_address) {
      return order.delivery_address;
    }
    
    if (order.userDetails?.address) {
      return order.userDetails.address;
    }
    
    return 'No address';
  };
  
  const getDeliveryDistance = (order) => {
    if (!order) return '-';
    
    if (order.deliveryDistance !== undefined && order.deliveryDistance !== null) {
      return `${parseFloat(order.deliveryDistance).toFixed(2)} km`;
    }
    
    return '-';
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      {...attributes}
      sx={{
        cursor: 'grab',
        '&:active': { cursor: 'grabbing' },
        '&:hover': { backgroundColor: 'action.hover' },
        ...(moved && {
          animation: 'rowSlideIn 0.5s cubic-bezier(0.22, 1, 0.36, 1) forwards',
          borderLeft: '3px solid #4caf50',
          '@keyframes rowSlideIn': {
            '0%':   { transform: 'translateX(-12px)', opacity: 0.5, backgroundColor: 'rgba(76,175,80,0.18)' },
            '60%':  { transform: 'translateX(3px)',   opacity: 1,   backgroundColor: 'rgba(76,175,80,0.10)' },
            '100%': { transform: 'translateX(0)',     opacity: 1,   backgroundColor: 'rgba(76,175,80,0.05)' },
          },
        }),
      }}
    >
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box {...listeners} sx={{ display: 'flex', alignItems: 'center' }}>
            <DragIcon color="action" />
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              ...(moved && {
                animation: 'numberPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
                '@keyframes numberPop': {
                  '0%':   { transform: 'scale(1)' },
                  '50%':  { transform: 'scale(1.5)', color: '#4caf50' },
                  '100%': { transform: 'scale(1)' },
                },
              }),
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {index + 1}
            </Typography>
            {moved && (
              <Typography
                variant="caption"
                sx={{
                  color: '#4caf50',
                  fontWeight: 'bold',
                  lineHeight: 1,
                  fontSize: '0.75rem',
                }}
              >
                {movedUp ? '▲' : '▼'}
              </Typography>
            )}
          </Box>
        </Box>
      </TableCell>
      <TableCell>
        <Chip 
          label={`#${orderId}`} 
          color={moved ? 'success' : 'primary'}
          size="small"
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {getTimeSlotDisplay(orderDetail)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography 
          variant="body2" 
          sx={{ 
            maxWidth: 200, 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'nowrap' 
          }}
          title={getDeliveryAddress(orderDetail)}
        >
          {getDeliveryAddress(orderDetail)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {getDeliveryDistance(orderDetail)}
        </Typography>
      </TableCell>
    </TableRow>
  );
};

const RiderAllocationModal = ({ open, onClose, selectedOrders = [], onAllocate }) => {
  const { actions } = useApp();
  const [riders, setRiders] = useState([]); // Search results
  const [searchLoading, setSearchLoading] = useState(false);
  const [allocating, setAllocating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRider, setSelectedRider] = useState(null);
  const [error, setError] = useState('');
  
  // Two-step flow state
  const [currentStep, setCurrentStep] = useState('route'); // 'route' or 'rider'
  const [orderedList, setOrderedList] = useState([]);
  const [createdRoute, setCreatedRoute] = useState(null);
  const [creatingRoute, setCreatingRoute] = useState(false);
  const [optimizingRoute, setOptimizingRoute] = useState(false);
  const [moveDeltas, setMoveDeltas] = useState({});
  
  // Order details state
  const [orderDetails, setOrderDetails] = useState({});
  const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);

  // Search riders via API (same as Riders list page)
  const handleSearchRiders = async () => {
    const search = (searchTerm || '').trim();
    if (!search) {
      actions.showSnackbar('Enter name or mobile number to search', 'warning');
      return;
    }
    setSearchLoading(true);
    setError('');
    setRiders([]);
    try {
      const response = await fetch(
        `${appConfig.api.baseURL}/users/riders/list?search=${encodeURIComponent(search)}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem(appConfig.storage.token)}`
          }
        }
      );
      if (!response.ok) throw new Error('Failed to fetch riders');
      const data = await response.json();
      if (data.success) {
        setRiders(data.riders || []);
        if (!(data.riders || []).length) {
          actions.showSnackbar('No riders found', 'info');
        }
      } else {
        setError(data.message || 'Search failed');
        actions.showSnackbar(data.message || 'Search failed', 'error');
      }
    } catch (err) {
      console.error('Error searching riders:', err);
      setError(err.message || 'Failed to search riders');
      actions.showSnackbar(err.message || 'Failed to search riders', 'error');
    } finally {
      setSearchLoading(false);
    }
  };

  // Fetch order details
  const fetchOrderDetails = async (orderIds) => {
    if (orderIds.length === 0) return;
    
    setLoadingOrderDetails(true);
    try {
      const details = {};
      
      // Fetch details for each order
      for (const orderId of orderIds) {
        try {
          const response = await fetch(`${appConfig.api.baseURL}/orders/${orderId}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem(appConfig.storage.token)}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              details[orderId] = data.order;
            }
          }
        } catch (error) {
          console.error(`Error fetching order ${orderId}:`, error);
        }
      }
      
      setOrderDetails(details);
    } catch (error) {
      console.error('Error fetching order details:', error);
    } finally {
      setLoadingOrderDetails(false);
    }
  };

  // Handle rider selection
  const handleRiderSelect = (rider) => {
    setSelectedRider(rider);
  };

  // Handle allocation
  const handleAllocateRider = async () => {
    if (!selectedRider) {
      actions.showSnackbar('Please select a rider', 'warning');
      return;
    }

    if (!createdRoute) {
      actions.showSnackbar('No route created for allocation', 'warning');
      return;
    }

    setAllocating(true);
    try {
      // Use bulk update orders API with rider data and route ID
      const url = `${appConfig.api.baseURL}/orders/bulk-update-orders`;
      console.log('Making request to:', url);
      console.log('Request payload:', {
        orderIds: orderedList,
        updateData: {
          deliveryPersonMobile: selectedRider.userId,
          deliveryPersonName: selectedRider.f_name,
          deliveryFrom: 'PILOT',
          routeId: createdRoute.routeId
        }
      });
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem(appConfig.storage.token)}`
        },
        body: JSON.stringify({
          orderIds: orderedList,
          updateData: {
            deliveryPersonMobile: selectedRider.userId,
            deliveryPersonName: selectedRider.f_name,
            deliveryFrom: 'PILOT',
            routeId: createdRoute.routeId,
            order_status: 'assigned'
          }
        })
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      
      if (!response.ok) {
        console.error('Request failed with status:', response.status);
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Response data:', data);
      
      if (data.success) {
        // Now update the route document with delivery person details
        console.log('Updating route with delivery person details');
        console.log('Selected rider object:', selectedRider);
        console.log('Route ID:', createdRoute.routeId);
        
        try {
          const routeUpdatePayload = {
            updateData: {
              deliveryPersonId: selectedRider.userId,
              deliveryPersonName: selectedRider.f_name,
              deliveryPersonMobile: selectedRider.mobileNo
            }
          };
          console.log('Route update payload:', routeUpdatePayload);
          
          const routeUpdateUrl = `${appConfig.api.baseURL}/delivery-routes/${createdRoute.routeId}`;
          console.log('Route update URL:', routeUpdateUrl);
          
          const routeUpdateResponse = await fetch(routeUpdateUrl, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem(appConfig.storage.token)}`
            },
            body: JSON.stringify(routeUpdatePayload)
          });

          if (!routeUpdateResponse.ok) {
            console.warn('Failed to update route with delivery person details');
          } else {
            console.log('Route updated successfully with delivery person details');
          }
        } catch (routeError) {
          console.warn('Error updating route with delivery person details:', routeError);
        }

        actions.showSnackbar(`Successfully allocated route with ${orderedList.length} orders to ${selectedRider.f_name}`, 'success');
        onAllocate(selectedRider, orderedList);
        handleClose();
      } else {
        actions.showSnackbar(data.message || 'Failed to allocate rider', 'error');
      }
    } catch (error) {
      console.error('Error allocating rider:', error);
      actions.showSnackbar('Error allocating rider', 'error');
    } finally {
      setAllocating(false);
    }
  };

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle order reordering via drag and drop
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setOrderedList((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // Optimize route order using Google Maps API (minimize travel distance)
  const handleOptimizeRoute = async () => {
    if (orderedList.length === 0) {
      actions.showSnackbar('No orders to optimize', 'warning');
      return;
    }
    if (orderedList.length > 23) {
      actions.showSnackbar('Cannot optimize more than 23 orders at once', 'warning');
      return;
    }
    setOptimizingRoute(true);
    try {
      const response = await fetch(`${appConfig.api.baseURL}/delivery-routes/optimize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem(appConfig.storage.token)}`
        },
        body: JSON.stringify({ orderIds: orderedList })
      });
      const data = await response.json();
      if (data.success && data.optimizedOrderIds) {
        // Compute how many positions each order moved (negative = moved up, positive = moved down)
        const deltas = {};
        data.optimizedOrderIds.forEach((id, newIdx) => {
          const oldIdx = orderedList.indexOf(id);
          deltas[id] = newIdx - oldIdx;
        });

        const anyMoved = Object.values(deltas).some(d => d !== 0);
        setOrderedList(data.optimizedOrderIds);

        if (anyMoved) {
          setMoveDeltas(deltas);
          setTimeout(() => setMoveDeltas({}), 2000);
          actions.showSnackbar('Route optimized for minimum travel distance!', 'success');
        } else {
          actions.showSnackbar('Sequence is already optimal!', 'info');
        }
      } else {
        actions.showSnackbar(data.message || 'Failed to optimize route', 'error');
      }
    } catch (error) {
      console.error('Error optimizing route:', error);
      actions.showSnackbar('Error optimizing route', 'error');
    } finally {
      setOptimizingRoute(false);
    }
  };

  // Handle route creation
  const handleCreateRoute = async () => {
    if (orderedList.length === 0) {
      actions.showSnackbar('No orders to create route', 'warning');
      return;
    }

    setCreatingRoute(true);
    try {
      const response = await fetch(`${appConfig.api.baseURL}/delivery-routes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem(appConfig.storage.token)}`
        },
        body: JSON.stringify({
          orderIds: orderedList
        })
      });

      const data = await response.json();
      if (data.success) {
        setCreatedRoute(data.route);
        setCurrentStep('rider');
        setRiders([]);
        setSearchTerm('');
        setSelectedRider(null);
        actions.showSnackbar('Route created successfully!', 'success');
      } else {
        actions.showSnackbar(data.message || 'Failed to create route', 'error');
      }
    } catch (error) {
      console.error('Error creating route:', error);
      actions.showSnackbar('Error creating route', 'error');
    } finally {
      setCreatingRoute(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    setSelectedRider(null);
    setSearchTerm('');
    setError('');
    setCurrentStep('route');
    setCreatedRoute(null);
    setOrderedList([]);
    setMoveDeltas({});
    setRiders([]);
    onClose();
  };

  // Initialize ordered list when modal opens
  useEffect(() => {
    if (open) {
      setOrderedList([...selectedOrders]);
      setCurrentStep('route');
      setCreatedRoute(null);
      setSelectedRider(null);
      setRiders([]);
      setSearchTerm('');
      setOrderDetails({});
      
      if (selectedOrders.length > 0) {
        fetchOrderDetails(selectedOrders);
      }
    }
  }, [open, selectedOrders]);

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RiderIcon color="primary" />
          <Typography variant="h6" component="span">
            {currentStep === 'route' ? 'Create Delivery Route' : 'Assign Rider to Route'}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {currentStep === 'route' 
            ? `Step 1: Arrange ${selectedOrders.length} order(s) in delivery sequence. Use "Optimize Route" to minimize travel distance.`
            : `Step 2: Select a rider for route with ${orderedList.length} order(s)`
          }
        </Typography>
      </DialogTitle>

      <DialogContent>
        {currentStep === 'route' ? (
          // Step 1: Route Creation and Order Arrangement
          <>
            {/* Orders Table with Drag and Drop */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Delivery Sequence (Drag rows to reorder):
              </Typography>
              
              {loadingOrderDetails ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ minWidth: 80 }}>Sequence</TableCell>
                          <TableCell sx={{ minWidth: 120 }}>Order ID</TableCell>
                          <TableCell sx={{ minWidth: 150 }}>Time Slot</TableCell>
                          <TableCell sx={{ minWidth: 200 }}>Delivery Address</TableCell>
                          <TableCell sx={{ minWidth: 100 }}>Distance</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <SortableContext
                          items={orderedList}
                          strategy={verticalListSortingStrategy}
                        >
                          {orderedList.map((orderId, index) => (
                            <SortableTableRow
                              key={orderId}
                              orderId={orderId}
                              index={index}
                              orderDetail={orderDetails[orderId]}
                              moveDelta={moveDeltas[orderId]}
                            />
                          ))}
                        </SortableContext>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </DndContext>
              )}
            </Box>

            {createdRoute && (
              <Alert severity="success" sx={{ mt: 2 }}>
                <Typography variant="subtitle2">
                  Route Created Successfully!
                </Typography>
                <Typography variant="body2">
                  Route ID: {createdRoute.routeId} with {orderedList.length} orders in sequence.
                </Typography>
              </Alert>
            )}
          </>
        ) : (
          // Step 2: Rider Selection
          <>
            {/* Route Info */}
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Route: {createdRoute?.routeId}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {orderedList.map((orderId, index) => (
                  <Chip 
                    key={orderId} 
                    label={`${index + 1}. #${orderId}`} 
                    size="small" 
                    color="primary" 
                  />
                ))}
              </Box>
            </Box>

            {/* Search Section */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Search by rider name or mobile number, then select from results.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Name or mobile number"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchRiders()}
                disabled={searchLoading}
              />
              <Button
                variant="outlined"
                onClick={handleSearchRiders}
                disabled={searchLoading}
                startIcon={searchLoading ? <CircularProgress size={18} /> : <SearchIcon />}
              >
                {searchLoading ? 'Searching...' : 'Search'}
              </Button>
            </Box>

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Rider Results - clickable list */}
            <Box sx={{ maxHeight: 280, overflow: 'auto' }}>
              {searchLoading && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>Searching...</Typography>
              )}
              {!searchLoading && riders.length === 0 && searchTerm.trim() && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No riders found. Try another search.</Typography>
              )}
              {!searchLoading && !searchTerm.trim() && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>Search for a rider first.</Typography>
              )}
              {!searchLoading && riders.map((rider) => {
                const isSelected = selectedRider?.userId === rider.userId;
                return (
                  <Box
                    key={rider.userId}
                    onClick={() => handleRiderSelect(rider)}
                    sx={{
                      py: 1.5,
                      px: 2,
                      mb: 0.5,
                      cursor: 'pointer',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      backgroundColor: isSelected ? 'action.selected' : 'transparent',
                      boxShadow: 1,
                      transition: 'background-color 0.2s, border-color 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        backgroundColor: isSelected ? 'action.selected' : 'action.hover',
                        boxShadow: 2
                      },
                      '&:active': {
                        transform: 'scale(0.99)'
                      }
                    }}
                  >
                    <Typography variant="body1">
                      {rider.f_name} {rider.l_name || ''} - {rider.mobileNo}
                    </Typography>
                    {rider.storeDetails && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {rider.storeDetails.name || rider.storeDetails.storeName}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>

            {/* Selected Rider Info */}
            {selectedRider && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="subtitle2">
                  Selected Rider: {selectedRider.f_name} {selectedRider.l_name || ''} ({selectedRider.mobileNo})
                </Typography>
                <Typography variant="body2">
                  Route with {orderedList.length} order(s) will be assigned to this rider.
                </Typography>
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={allocating || creatingRoute}>
          Cancel
        </Button>
        {currentStep === 'route' ? (
          <>
            <Button
              onClick={handleOptimizeRoute}
              variant="outlined"
              disabled={optimizingRoute || creatingRoute || orderedList.length === 0 || orderedList.length > 23}
              startIcon={optimizingRoute ? <CircularProgress size={20} /> : <RouteIcon />}
            >
              {optimizingRoute ? 'Optimizing...' : 'Optimize Route'}
            </Button>
            <Button
              onClick={handleCreateRoute}
              variant="contained"
              disabled={creatingRoute || orderedList.length === 0}
              startIcon={creatingRoute ? <CircularProgress size={20} /> : null}
            >
              {creatingRoute ? 'Creating Route...' : `Create Route (${orderedList.length} orders)`}
            </Button>
          </>
        ) : (
          <Button 
            onClick={handleAllocateRider} 
            variant="contained"
            disabled={!selectedRider || allocating || !createdRoute}
            startIcon={allocating ? <CircularProgress size={20} /> : null}
          >
            {allocating ? 'Allocating...' : `Assign Route to ${selectedRider?.f_name || 'Rider'}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RiderAllocationModal;
